import React, { memo, useEffect } from "react";

import { Disclosure } from "@headlessui/react";
import useMileageRequest from "@hooks/reimbursments/useMileageRequest";
import { useNotification } from "@hooks/useNotification";
import { useReduxSelector } from "@hooks/useReduxSelector";
import { SvgCalendarBlack, SvgNewFile, Caret } from "@routes/IconeSvg";
import moment from "moment-timezone";
import { useForm, Controller } from "react-hook-form";

import ModalIndex from "@Modals/layout/index";
import DefaultDatePicker from "@components/arhDatePicker/DefaultDatePicker";
import InputFileMultipleArh from "@components/input/InputFileMultiple";
import DisclosureItinerary from "@components/reimbursments/DisclosureItinerary";
import GoogleMap from "@components/reimbursments/GoogleMap";
import ListBoxSingleChoiceTree from "@components/tailwindComponents/ListboxForm/ListBoxSingleChoiceTree";
import {
  ListboxSimple,
  CheckboxSimple,
  Input,
  InputGroup,
  TextArea,
} from "@components/tailwindComponents";
import { Switch } from "@components/tailwindComponents";

import { MODE, REGEX_VALID_FILES } from "@helpers/_constants";
import { checkUserId, convertToNumericString } from "@helpers/_functions";

export default memo(function MileinageRequest({
  onClickClose,
  isOpen,
  reimbursment = {},
  t,
}) {
  const { username } = useReduxSelector(({ userConnected }) => userConnected);
  const { showInfo } = useNotification();
  const {
    watch,
    control,
    handleSubmit,
    clearErrors,
    reset,
    setValue,
    setError,
    trigger
  } = useForm({
    mode: "onSubmit",
    reValidateMode: "onChange",
  });

  const [itinerary, avoidsMode = [], date, categorySelected] = watch([
    "itinerary",
    "avoidsMode",
    "date",
    "category",
  ]);

  const {
    oldFiles,
    category,
    useGoogleMap,
    coords,
    unitySystem,
    directionsResponse,
    isSaved,
    recentItineraries,
    itineraries,
    accounts,
    projects,
    handleFormError,
    onAvoid,
    onSaveClicked,
    onValidateItinerary,
    onInit,
    onUpdateInit,
    setEmployeeId,
    setUseGoogleMap,
    handleUploadStatusChange,
    handleDeleteOldFiles,
    resetInputFiles,
  } = useMileageRequest({
    userId: localStorage.getItem("userId"),
    useForm: {
      reset,
      watch,
      setValue,
      clearErrors,
      setError,
      trigger
    },
    t,
    onClickClose,
    reimbursment,
    isOpen,
    commonName: username,
  });

  useEffect(() => {
    if (!isOpen) {
      onInit();
      resetInputFiles();
    } else if (reimbursment?._id) {
      onUpdateInit();
    } else {
      setUseGoogleMap(true);
      setEmployeeId(localStorage.getItem("userId"));
    }
  }, [isOpen]);
  
  return (
    <ModalIndex
      isOpen={isOpen}
      title={t("Demande de remboursement kilométrique")}
      isLoading={isSaved}
      onClickClose={onClickClose}
      buttonOptions={{
        click: handleSubmit(onSaveClicked, handleFormError),
        color: "green",
        title: t("Enregistrer"),
      }}
      buttonCancelOptions={{
        click: () => onClickClose(),
        color: "outlined",
        title: t("Annuler"),
      }}
    >
      <div className="!-arh-mt-4 arh-flex arh-items-center arh-justify-center arh-space-x-2.5">
        <span>{SvgCalendarBlack()}</span>
        <span className="arh-mt-1 arh-font-poppins arh-text-[13px] arh-font-medium arh-text-darkColor">
          {moment(date).isValid() ? moment(date).format("LL") : "-"}
        </span>
      </div>
      {useGoogleMap && (
        <div className="!arh-my-2 arh-h-[156px] arh-w-full arh-border-0">
          <GoogleMap
            coords={coords}
            width="100%"
            height="100%"
            directionsResponse={directionsResponse}
          />
        </div>
      )}

      <div className="arh-flex arh-flex-col arh-space-y-2">
        <div className="arh-mt-2 arh-flex arh-justify-center arh-font-poppins arh-font-medium arh-text-darkColor ">
          <div className="arh-flex arh-items-center arh-space-x-4">
            <label className="!arh-mb-0 arh-text-xs">
              {t("Itinéraire automatique")}
            </label>
            <Switch
              switchDouble={true}
              color="arh-bg-greenColor"
              checked={useGoogleMap}
              rotate
              handleChange={() => {
                if (!reimbursment?._id) {
                  setUseGoogleMap(!useGoogleMap);
                  onInit();
                  setEmployeeId(localStorage.getItem("userId"));
                } else {
                  showInfo(
                    "Le changement est impossible en raison du risque de confusion des données",
                  );
                }
              }}
            />
            <label className="!arh-mb-0 arh-text-xs">
              {t("Saisie manuelle")}
            </label>
          </div>
        </div>
        <Controller
            control={control}
            name="account"
            defaultValue={null}
            //rules={{ required: true }}
            render={({
              field: { onChange, value, name },
              //fieldState: { invalid }
            }) => (
              <ListboxSimple
                placeholder={t("Compte utilisé")}
                name={name}
                labelActive
                label={t("Compte utilisé")}
                optionList={accounts || []}
                selectedValue={value}
                setSelectedValue={onChange}
                //showReset={!!value._id}
                //invalid={invalid}
                noRadio
                withTooltip
                TooltipLabel
              />
            )}
          />
        <Controller
          control={control}
          name="category"
          defaultValue={null}
          rules={{ required: true }}
          render={({
            field: { onChange, value, name },
            fieldState: { invalid },
          }) => (
            <ListboxSimple
              placeholder={t("Catégorie")}
              name={name}
              labelActive={true}
              label={t("Catégorie")}
              optionList={category || []}
              selectedValue={value}
              setSelectedValue={onChange}
              //showReset={!!value}
              invalid={invalid}
              noRadio={true}
              withTooltip={true}
            />
          )}
        />
        <Controller
          control={control}
          name="project"
          defaultValue={null}
          render={({
            field: { onChange, value }
          }) => (
            <ListBoxSingleChoiceTree
              placeholder={t("Projet")}
              title={t("Projet")}
              items={projects || []}
              value={value}
              onSelectionChange={(selectedProject) => {
                onChange(selectedProject);
                setValue("reference", (selectedProject?.reference || "").trim());
                clearErrors("reference");
              }}
              withSearch
            />
          )}
        />
        <Controller
          control={control}
          name="reference"
          render={({ field: { onChange, value, name } }) => (
            <Input
              label={t("Référence")}
              placeholder={t("Référence")}
              value={value}
              onChange={onChange}
              name={name}
            />
          )}
        />
        <Controller
          control={control}
          name="date"
          rules={{ required: true }}
          render={({
            field: { onChange, value, name },
            fieldState: { invalid },
          }) => (
            <div
              className={`flex-column calendar_with_btn employerThemeDatePicker arh-calendar-reimbursment-mileinage arh-mb-[1.75rem] arh-flex arh-w-full  ${
                invalid ? "arh-text-[#F60A20]" : "arh-text-darkColor"
              } `}
            >
              <label
                className={`arh-mb-1 arh-flex arh-w-full arh-font-poppins arh-text-xs arh-font-medium`}
              >
                {t("Date")}
              </label>

              <DefaultDatePicker
                className="focus_green dateOfBirth arh-relative"
                name={name}
                placeholder={"Date"}
                dateValue={value}
                setDateValue={onChange}
                classPosition="calendar_left arh-w-full"
                invalid={invalid}
              />
            </div>
          )}
        />
        {useGoogleMap && (
          <>
            <Controller
              control={control}
              name="itinerary"
              rules={{ required: true }}
              render={({
                field: { onChange, value, name },
                fieldState: { invalid },
              }) => (
                <ListboxSimple
                  placeholder={t("Itinéraires récents")}
                  name={name}
                  labelActive={true}
                  label={t("Itinéraires récents")}
                  optionList={recentItineraries || []}
                  selectedValue={value}
                  setSelectedValue={onChange}
                  showReset={!!itinerary?.name}
                  invalid={invalid}
                  noRadio={true}
                  withTooltip={true}
                />
              )}
            />

            <div className="arh-bg-greyColor arh-p-2">
              <DisclosureItinerary
                t={t}
                watch={watch}
                control={control}
                itineraries={itineraries}
                userId={checkUserId()}
                onCalcul={onValidateItinerary}
                clearErrors={clearErrors}
              />
            </div>
            <div className="arh-flex arh-flex-wrap arh-items-center arh-justify-between arh-border arh-border-x-0 arh-border-solid arh-border-[#EAEAEA] arh-p-3">
              <span className="arh-font-poppins arh-text-xs arh-font-medium">
                {t("Eviter")}
              </span>
              <div className="arh-flex arh-flex-wrap arh-items-center arh-justify-between">
                <Controller
                  control={control}
                  name="avoidsMode"
                  render={({ field: { onChange } }) =>
                    MODE.map((_mode) => (
                      <CheckboxSimple
                        key={_mode._id}
                        label={t(_mode.label)}
                        checked={avoidsMode?.some(
                          (_item) => _item._id === _mode._id,
                        )}
                        className="hover:arh-bg-transparent active:arh-bg-transparent"
                        onClick={() => onAvoid(_mode)}
                      />
                    ))
                  }
                />
              </div>
            </div>
          </>
        )}

        <div className="arh-flex arh-items-center arh-justify-between arh-gap-2 mobile-768:arh-flex-col  mobile-768:arh-space-y-2">
          <Controller
            control={control}
            name="amount"
            rules={{
              required: true,
              validate: (value) => value > 0,
            }}
            render={({ field: { value, name }, fieldState: { invalid } }) => (
              <Input
                label={t("A rembourser")}
                classParent="arh-flex arh-flex-col mobile-768:arh-w-full arh-w-[33%] "
                name={name}
                additionnalClas="!arh-font-medium mobile-768:arh-w-full"
                placeholder={t("Montant")}
                value={`${value || 0} ${
                  categorySelected?.slices[0]?.devise?._id || "$"
                }`}
                invalid={invalid}
                disabled
                useToolTip
              />
            )}
          />

          <Controller
            control={control}
            name="distance"
            rules={{
              required: true,
              validate: (value) => value > 0,
            }}
            render={({
              field: { value, name, onChange },
              fieldState: { invalid },
            }) => (
              <InputGroup
                name={name}
                content={unitySystem}
                classContent="arh-w-12"
                classParent="mobile-768:arh-w-full arh-w-[33%]"
                label={t("Distance")}
                onChange={(e) =>
                  onChange(convertToNumericString(e.target.value))
                }
                value={value}
                disabled={!!useGoogleMap}
                invalid={invalid}
                useToolTip={!!useGoogleMap}
                classInput=" !arh-pr-0 "
              />
            )}
          />
          <Controller
            control={control}
            name="totalDistance"
            render={({ field: { value, name } }) => (
              <InputGroup
                name={name}
                content="Km"
                label={t("Total par année")}
                value={value}
                disabled
                classContent="arh-w-12"
                classParent="mobile-768:arh-w-full arh-w-[33%]"
                useToolTip
                classInput=" !arh-pr-0 "
              />
            )}
          />
        </div>
        <div>
          <Controller
            control={control}
            name="description"
            render={({ field: { onChange, value, name } }) => (
              <Input
                label={t("Description")}
                name={name}
                value={value}
                onChange={onChange}
                placeholder={t("Description")}
              />
            )}
          />
        </div>
        <div>
          <Controller
            control={control}
            name="commentSended"
            defaultValue=""
            render={({ field: { onChange, value, name } }) => (
              <TextArea
                label={t("Commentaire")}
                name={name}
                onChange={onChange}
                placeholder={t("Commentaire")}
                value={value}
              />
            )}
          />
        </div>
        <Disclosure>
          {({ open }) => (
            <>
              <Disclosure.Button
                className={`arh-mt-2 arh-flex arh-w-full arh-items-center arh-justify-between arh-rounded-md arh-border arh-border-solid arh-border-greyBorderInput arh-bg-transparent arh-p-4 ${
                  open &&
                  "arh-rounded-bl-none arh-rounded-br-none arh-border-b-0"
                }`}
              >
                <div className="arh-flex arh-items-center arh-space-x-3">
                  <span>{SvgNewFile()}</span>
                  <span className="arh-font-poppins arh-text-xs arh-font-medium arh-text-darkColor">
                    {t("Ajouter un fichier")}
                  </span>
                </div>
                <span>
                  <Caret
                    className={`${open ? "arh-rotate-90" : "arh-rotate-0"}`}
                    aria-hidden="true"
                  />
                </span>
              </Disclosure.Button>
              <Disclosure.Panel
                className={`input_file_theme_employer  arh-rounded-md arh-border arh-border-solid arh-border-greyBorderInput arh-p-4 ${
                  open &&
                  "arh-rounded-tl-none arh-rounded-tr-none arh-border-t-0"
                }`}
              >
                <Controller
                  control={control}
                  name="files"
                  defaultValue={[]}
                  render={({ field: { onChange, value } }) => (
                    <InputFileMultipleArh
                      handleChangeStatus={handleUploadStatusChange(
                        value,
                        onChange,
                      )}
                      onDeleteOldFile={handleDeleteOldFiles}
                      classUploadFile="employer_theme_upload GreenThemeSelect !arh-mb-0"
                      regexp={REGEX_VALID_FILES}
                      hideTitle={true}
                      allFile={true}
                      dataFile={oldFiles}
                      isLoading={isSaved}
                    />
                  )}
                />
              </Disclosure.Panel>
            </>
          )}
        </Disclosure>
      </div>
    </ModalIndex>
  );
});
